import React from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import QRCode from "qrcode.react";
import consts from "../../main/consts";
import { getLabelOrigem } from "../../main/origem";
import AmericanasLogo from "../../images/AmericanasLogo";
import TesouraIcon from "../../images/TesouraIcon";
import moment from "moment";
import { useRemoteConfig } from "../../state/remoteConfig/RemoteConfigContext";
import { remoteConfigTypes } from "../../state/remoteConfig/types";
import { getEansFormatados } from "../../utils/eans";

const useStyles = makeStyles(() => ({
    root: {
        width: "100%",
        height: "100%",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '24px',
        maxWidth: 600,
        margin: "0 auto",
        boxSizing: "border-box",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        width: '160px',
        height: "auto",
    },
    headerRight: {
        textAlign: "right",
    },
    canalVendas: {
        fontSize: '14px',
        lineHeight: "125%",
        fontWeight: 700,
        letterSpacing: '0.25px',
        color: "rgba(26, 26, 26, 0.60)",
    },
    codigoExterno: {
        fontSize: '14px',
        lineHeight: "125%",
        fontWeight: 700,
        letterSpacing: '0.25px',
        color: "rgba(26, 26, 26, 0.60)",
    },
    infoSection: {
        display: "flex",
        gap: '16px',
        justifyContent: "center",
        alignItems: "flex-start",
    },
    infoLeft: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: '8px',
        flex: '1 0 0'
    },
    infoRight: {
        width: '180px',
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        alignSelf: "stretch",
    },
    letraRecebedor: {
        fontSize: '28px',
        fontWeight: 600,
        marginBottom: '24px',
        lineHeight: 'normal',
        color: "rgba(26, 26, 26, 0.87)",
    },
    infoRow: {
        fontSize: '14px',
        lineHeight: "20px",
        fontWeight: 400,
        letterSpacing: '0.15px',
        color: "rgba(26, 26, 26, 0.87)",
    },
    bold: {
        fontWeight: 700,
        color: "rgba(26, 26, 26, 0.87)",
    },
    qrSection: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 152,
    },
    qrLabel: {
        fontSize: '12px',
        lineHeight: "17px",
        fontWeight: 400,
        textAlign: "center",
        marginTop: '8px',
        color: "rgba(26, 26, 26, 0.87)",
    },
    cutDivider: {
        position: "relative",
        width: "100%",
        height: 32,
        display: "flex",
        alignItems: "center",
    },
    cutLine: {
        width: "100%",
        borderTop: "2px dashed rgba(26, 26, 26, 0.60)",
    },
    cutIconWrapper: {
        position: "absolute",
        left: 74,
        top: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    cutText: {
        fontSize: '12px',
        lineHeight: "17px",
        textAlign: "center",
        color: "rgba(26, 26, 26, 0.60)",
        letterSpacing: '0.4px',
    },
    itemsHeader: {
        fontSize: "14px",
        lineHeight: "17px",
        fontWeight: 700,
        letterSpacing: '0.25px',
        color: "rgba(26, 26, 26, 0.87)",
    },
    table: {
        display: "flex",
        flexDirection: "column",
        gap: '12px',
    },
    tableCol: {
        width: "50%",
        padding: '0px',
        borderBottom: 'none !important',
        "&:nth-child(odd)": {
            paddingRight: "16px !important",
        },
        "&:nth-child(even)": {
            paddingRight: "0 !important",
            paddingLeft: "16px !important",
        },
    },
    tableItem: {
        fontSize: '12px',
        color: "rgba(26, 26, 26, 0.87)",
        lineHeight: "17px",
        letterSpacing: '0.4px',
    },
    '@media print': {
        root: {
            padding: "22px 40px 24px",
        },
    },
}));

function PrintReceipt({ order, nfUrlReceitaFederal }) {
    const classes = useStyles();
    const { getGlobalConfig } = useRemoteConfig();

    if (!order) return null;

    const dataCriacaoPedido = moment(order.dataCriacao).format("DD/MM/YYYY");
    const dataEntregaEstimada = moment(order.dhEntregaEstimada).format("DD/MM/YYYY");
    const labelOrigem = getLabelOrigem(order.origem) || "";
    const modalidadeEntrega = labelOrigem
        ? `${labelOrigem}${order.tipoEntrega === "SAME_STATE" ? " - Estadual" : ""}`
        : "";

    const notaFiscalUrl = getGlobalConfig ? (getGlobalConfig(remoteConfigTypes.GLOBAL_CONFIG.CONSULTA_NOTA_FISCAL_URL) || consts.CONSULTA_NOTA_FISCAL_URL) : consts.CONSULTA_NOTA_FISCAL_URL;

    const nfUrlQrcode =
        order.notaFiscal && order.notaFiscal.chave
            ? nfUrlReceitaFederal + order.notaFiscal.chave
            : `${notaFiscalUrl}?codigoExterno=${order.codigoExterno}&hash=${order.hashPublico}`;

    const enderecoCompleto = order.cliente
        ? [
            order.cliente.endereco,
            order.cliente.numero,
            order.cliente.complemento,
            order.cliente.bairro,
        ]
            .filter(Boolean)
            .join(", ") +
        (order.cliente.cidade ? `, ${order.cliente.cidade}` : "") +
        (order.cliente.uf ? ` - ${order.cliente.uf}` : "") +
        (order.cliente.cep ? ` CEP ${order.cliente.cep}` : "")
        : "";

    const itensDoPedido =
        order.itens &&
        order.itens.filter(
            (p) => p.quantidadeTotal == null || p.quantidadeTotal > 0
        );

    const itensFormatadosParaListagem = itensDoPedido && itensDoPedido.length > 0 ?
        itensDoPedido.reduce((rows, item, index) => {
            if (index % 2 === 0) {
                rows.push(itensDoPedido.slice(index, index + 2));
            }
            return rows;
        }, []) : [];

    return (
        <Box className={classes.root}>
            {/* Cabeçalho */}
            <Box className={classes.header}>
                <AmericanasLogo />
                <Box className={classes.headerRight}>
                    {order.canalDeVendas && (
                        <Typography className={classes.canalVendas}>
                            Pedido via {order.canalDeVendas}
                        </Typography>
                    )}
                    <Typography className={classes.codigoExterno}>
                        {order.codigoExterno}
                    </Typography>
                </Box>
            </Box>

            {/* Informações do cliente + QR Code */}
            <Box className={classes.infoSection}>
                <Box className={classes.infoLeft}>
                    {modalidadeEntrega && (
                        <Typography className={classes.infoRow}>
                            Modalidade de entrega:{" "}
                            <span className={classes.bold}>{modalidadeEntrega.toLowerCase()}</span>
                        </Typography>
                    )}
                    <Typography className={classes.infoRow}>
                        Criação do pedido:{" "}
                        <span className={classes.bold}>{dataCriacaoPedido}</span>
                    </Typography>
                    <Typography className={classes.infoRow}>
                        Entrega estimada:{" "}
                        <span className={classes.bold}>{dataEntregaEstimada}</span>
                    </Typography>
                    <Typography className={classes.infoRow}>
                        Nome do cliente:{" "}
                        <span className={classes.bold}>
                            {order.cliente && order.cliente.nome}
                        </span>
                    </Typography>
                    <Typography className={classes.infoRow}>
                        Nome do recebedor:{" "}
                        <span className={classes.bold}>
                            {order.recebedor && order.recebedor.nome}
                        </span>
                    </Typography>
                    <Typography className={classes.infoRow}>
                        Endereço:{" "}
                        <span className={classes.bold}>{enderecoCompleto}</span>
                    </Typography>
                </Box>

                <Box className={classes.infoRight}>
                    {/* Letra do recebedor */}
                    <Typography className={classes.letraRecebedor}>
                        {order.recebedor && order.recebedor.nome
                            ? order.recebedor.nome.charAt(0).toUpperCase()
                            : ""}
                    </Typography>

                    {/* QR Code da Nota Fiscal */}
                    {nfUrlQrcode && (
                        <Box className={classes.qrSection}>
                            <QRCode value={nfUrlQrcode} size={106} />
                            <Typography className={classes.qrLabel}>
                                Escaneie o código acima para acessar a Nota Fiscal.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Linha de recorte */}
            <Box>
                <Box className={classes.cutDivider}>
                    <Box className={classes.cutLine} />
                    <Box className={classes.cutIconWrapper}>
                        <TesouraIcon />
                    </Box>
                </Box>
                <Typography className={classes.cutText}>
                    Recorte aqui! A parte inferior não deve ser
                    <br />
                    disponibilizada para o cliente/entregador.
                </Typography>
            </Box>

            {/* Itens do pedido */}
            <Typography className={classes.itemsHeader}>
                {order.codigoExterno}
            </Typography>
            {itensFormatadosParaListagem && itensFormatadosParaListagem.length > 0 && (
                <Table size="small" className={classes.table}>
                    <TableBody>
                        {itensFormatadosParaListagem.map((par, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {par.map((item, colIndex) => (
                                    <TableCell
                                        key={item.codigoEAN + "-" + colIndex}
                                        className={classes.tableCol}
                                    >
                                        <Typography className={classes.tableItem}>
                                            {item.quantidadeTotal || item.quantidade}x {item.descricao}{" "}
                                            (EAN: {getEansFormatados(item)})
                                        </Typography>
                                    </TableCell>
                                ))}

                                {par.length === 1 && <TableCell className={classes.tableCol} width="50%" />}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}

export default PrintReceipt;
